import { useState } from 'react';
import { useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';

/**
 * True when the transcription agent joined this room and has since gone,
 * i.e. the live transcript really has stopped advancing.
 *
 * Two things make this less obvious than "is the bot in the participant
 * list right now":
 *
 * 1. Remote-participant state is only meaningful while we are connected.
 *    On a FULL reconnect `Room.handleRestarting` deliberately unwinds
 *    every remote participant ("also unwind existing participants &
 *    existing subscriptions") before the signal session comes back, so
 *    `botPresent` reads false for a moment even though the agent never
 *    left the room. `applyJoinResponse` repopulates the participants
 *    before the state returns to Connected, so gating on Connected is
 *    enough to skip that window. Prod logs show practitioners full
 *    reconnecting mid-session (participant closing and starting an RTC
 *    session in the same second) while the agent job stayed up and kept
 *    resubscribing, which is exactly the false positive this avoids.
 *
 * 2. The warning has to be able to switch back off. The agent rejoins on
 *    its own, so a flag that only ever latches on would keep claiming the
 *    transcript is paused long after it resumed.
 */
export function useBotLeftMidCall(botPresent: boolean): boolean {
  const connectionState = useConnectionState();
  const connected = connectionState === ConnectionState.Connected;

  const [botSeen, setBotSeen] = useState(false);
  const [leftMidCall, setLeftMidCall] = useState(false);

  // Derive during render (the React "adjust state on prop change" pattern)
  // rather than in an effect, matching the rest of this feature.
  if (connected && botPresent) {
    if (!botSeen) setBotSeen(true);
    if (leftMidCall) setLeftMidCall(false);
  }
  if (connected && !botPresent && botSeen && !leftMidCall) {
    setLeftMidCall(true);
  }

  return leftMidCall;
}
